/**
 * TypeScript interfaces for the Event Explorer feature.
 * Models the Chargebee Events API response shapes and internal state.
 */

// ─── API Response Types ──────────────────────────────────────────────────────

export interface ChargebeeEvent {
  id: string;
  event_type: string;
  occurred_at: number; // Unix timestamp
  source: EventSource;
  api_version: 'v1' | 'v2';
  user: string | null;
  origin_user?: string | null;
  content: Record<string, unknown>;
  webhooks?: EventWebhook[];
}

export interface EventWebhook {
  id: string;
  webhook_status: WebhookStatus;
  pc_version?: string;
  chargebee_response_schema_type?: string;
}

export type EventSource =
  | 'admin_console'
  | 'api'
  | 'hosted_page'
  | 'portal'
  | 'scheduled_job'
  | 'system'
  | 'js_api'
  | 'bulk_operation'
  | 'external_service'
  | 'none';

export type WebhookStatus =
  | 'scheduled'
  | 'succeeded'
  | 'failed'
  | 're_scheduled'
  | 'not_configured'
  | 'not_applicable'
  | 'skipped';

export interface ListEventsResponse {
  list: Array<{ event: ChargebeeEvent }>;
  next_offset?: string;
}

export interface RetrieveEventResponse {
  event: ChargebeeEvent;
}

// ─── Service Types ───────────────────────────────────────────────────────────

export type FilterOperator = 'is' | 'is_not' | 'in' | 'not_in';
export type TimestampFilterOperator = 'before' | 'after' | 'on' | 'between';
export type IdFilterOperator =
  | 'is'
  | 'is_not'
  | 'starts_with'
  | 'in'
  | 'not_in';

export interface ListEventsParams {
  siteId: string;
  siteName: string;
  apiKey: string;
  limit?: number;
  offset?: string;
  sortBy?: 'asc' | 'desc';
  filters?: EventApiFilters;
}

export interface EventApiFilters {
  eventType?: { operator: FilterOperator; value: string | string[] };
  source?: { operator: FilterOperator; value: string | string[] };
  webhookStatus?: { operator: FilterOperator; value: string | string[] };
  occurredAt?: {
    operator: TimestampFilterOperator;
    value: number | [number, number];
  };
  id?: { operator: IdFilterOperator; value: string | string[] };
}

// ─── UI State Types ──────────────────────────────────────────────────────────

export interface EventFilters {
  source: string[];
  eventType: string[];
  timeRangePreset: string;
  dateFrom: number | null; // Unix timestamp (seconds)
  dateTo: number | null; // Unix timestamp (seconds)
  webhookStatus: string | null;
  searchQuery: string;
}

export const DEFAULT_EVENT_FILTERS: EventFilters = {
  source: [],
  eventType: [],
  timeRangePreset: '90d',
  dateFrom: null,
  dateTo: null,
  webhookStatus: null,
  searchQuery: '',
};

export type DetailTab = 'json' | 'webhooks' | 'forward';
